/**
 * Slicer for Application-wide data
 * -seasons
 * -userInfo
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import _ from "lodash"
import { Version } from "../api/Version"
import { Countries } from "../api/Countries"
import { Seasons } from "../api/Seasons"
import { ServiceTypes } from "../api/ServiceTypes"
import { ReasonCodes } from "../api/ReasonCodes"

export interface ISeasonData {
  code: string
  startDate: string
  endDate: string
}

export interface ICountryData {
  code: string
  name: string
}

export interface IServiceType {
  code: string
  description: string
}

export interface IReasonCode {
  code: string
  description: string
}

export interface IUserInfo {
  loginName: string
  authenticated: boolean
}

export interface IMenuItem {
  name: string
  pageId: string
  items: IMenuItem[]
}

export interface IMenuData {
  items: IMenuItem[]
  isAuth: boolean
}

const appSlice = createSlice({
  name: "seasonData",

  initialState: {
    versionInfo: {
      backupSiteName: "",
      backupSiteUrl: "",
      version: "",
      siteName: "",
      handlingApplication: "",
      useHeaderAuth: false
    },
    seasons: [],
    countries: [],
    serviceTypes: [],
    reasonCodes: [],
    userInfo: {
      loginName: undefined,
      authenticated: true // not false to avoid redirect to login if user presses F5 on a private route
    },
    menu: { items: [], isAuth: false }
  },

  reducers: {
    loadVersion: (state, action: PayloadAction<Version>) => {
      const _payload = action.payload
      state["versionInfo"] = {
        backupSiteName: _payload.backupSiteName,
        backupSiteUrl: _payload.backupSiteUrl,
        version: _payload.version,
        siteName: _payload.siteName,
        handlingApplication: _payload.handlingApplication,
        useHeaderAuth: _payload.useHeaderAuth
      }
    },

    loadSeasons: (state, action: PayloadAction<Seasons>) => {
      let data: ISeasonData[] = _.map(action.payload, (el) => {
        return {
          code: el.code,
          startDate: el.startDate,
          endDate: el.endDate
        }
      })

      state["seasons"] = data
    },

    loadCountries: (state, action: PayloadAction<Countries>) => {
      let data: ICountryData[] = _.map(action.payload, (el) => {
        return {
          code: el.code,
          name: el.name
        }
      })

      state["countries"] = data
    },

    loadServiceTypes: (state, action: PayloadAction<ServiceTypes>) => {
      let data: IServiceType[] = _.map(action.payload, (el) => {
        return {
          code: el.code,
          description: el.description
        }
      })

      state["serviceTypes"] = data
    },

    loadReasonCodes: (state, action: PayloadAction<ReasonCodes>) => {
      let data: IReasonCode[] = _.map(action.payload, (el) => {
        return {
          code: el.code,
          description: el.description
        }
      })

      state["reasonCodes"] = data
    },

    logonUser: (state, action: PayloadAction<IUserInfo>) => {
      state["userInfo"] = action.payload
    },

    logoutUser: (state, action) => {
      state["userInfo"] = {
        loginName: "",
        authenticated: false
      }
    },

    loadMenu: (state, action: PayloadAction<IMenuData>) => {
      state["menu"] = action.payload
    }
  }
})

export const {
  loadVersion,
  loadSeasons,
  loadCountries,
  loadServiceTypes,
  loadReasonCodes,
  logonUser,
  logoutUser,
  loadMenu
} = appSlice.actions
export default appSlice.reducer
